import { LeadGenerationRecordCreate, LeadGenerationType } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { CreateLeadGenerationPayload } from 'noddi-types';
import { cn } from 'noddi-ui-common';
import { NavioOverlayActions, useAppForm } from 'noddi-ui-web';
import { LEAD_GENERATION_TYPE_OPTIONS } from 'noddi-util';
import { z } from 'zod';
import { TanstackServiceDepartmentIdSelect } from '../ServiceDepartment/TanstackServiceDepartmentIdSelect';

const FORM_ID = 'lead-generation-create-form';

type Props = {
  initialValues?: Partial<CreateLeadGenerationPayload>;
  onSuccess?: (createdLeadId?: number) => void;
  submitLabel?: string;
  className?: string;
};

const validationSchema = z.object({
  description: z.string().min(1, 'Required'),
  type: z
    .string()
    .optional()
    .refine((val) => !!val, { message: 'Required' }),
  serviceDepartmentId: z
    .number()
    .optional()
    .refine((val) => val !== undefined, { message: 'Required' }),
  bookingItemId: z.number().nullable(),
  image: z.instanceof(File).nullable()
});

type FormValues = z.input<typeof validationSchema>;

export const TanstackLeadGenerationCreateForm = ({
  initialValues,
  onSuccess,
  submitLabel = 'Create lead',
  className
}: Props) => {
  const defaultValues: FormValues = {
    description: initialValues?.description ?? '',
    type: initialValues?.type ?? undefined,
    serviceDepartmentId: initialValues?.serviceDepartmentId ?? undefined,
    bookingItemId: initialValues?.bookingItemId ?? null,
    image: null
  };

  const { mutateAsync: postAttachImage } = noddiAsync.usePost({
    type: URLKeys.postAttachImageToLeadGeneration
  });

  const { mutateAsync: postCreateLead } = noddiAsync.usePost({
    type: URLKeys.postCreateLeadGeneration
  });

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: validationSchema
    },
    onSubmit: async ({ value }) => {
      const payload: LeadGenerationRecordCreate = {
        description: value.description,
        type: value.type as LeadGenerationType,
        serviceDepartmentId: value.serviceDepartmentId!,
        bookingItemId: value.bookingItemId ?? undefined
      };
      const response = await postCreateLead(payload);
      const createdId = response?.data?.id as number | undefined;

      if (createdId && value.image) {
        try {
          await postAttachImage({ leadGenerationId: createdId, image: value.image });
        } catch {
          // noop: lead created even if upload fails; users can retry from detail page
        }
      }

      onSuccess?.(createdId);
    }
  });

  return (
    <form.AppForm>
      <form
        id={FORM_ID}
        className={cn('flex flex-col gap-4', className)}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.AppField name='type'>
          {(field) => <field.Select label='Type' options={LEAD_GENERATION_TYPE_OPTIONS} />}
        </form.AppField>
        <form.AppField name='serviceDepartmentId'>{() => <TanstackServiceDepartmentIdSelect />}</form.AppField>
        <form.AppField name='bookingItemId'>
          {(field) => <field.Input label='Booking item ID (optional)' type='number' />}
        </form.AppField>
        <form.AppField name='description'>{(field) => <field.TextArea label='Description' rows={4} />}</form.AppField>
        <form.AppField name='image'>{(field) => <field.ImageUpload />}</form.AppField>
        <NavioOverlayActions>
          <form.SubscribeButton formId={FORM_ID} label={submitLabel} />
        </NavioOverlayActions>
      </form>
    </form.AppForm>
  );
};
